import { useMemo, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";

import {
  CodeText,
  FilterCard,
  InfoTile,
  PageHeader,
  QueryErrorState,
  StatusPill,
  TableEmptyRow,
  TableShell,
} from "@/components/common";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNullableDateTimeWithSeconds } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type CalendarOverview = RouterOutputs["admin"]["calendar"]["overview"];
type CalendarData = RouterOutputs["admin"]["calendar"]["list"];
type CalendarEvent = CalendarData["events"][number];
type CalendarSync = CalendarData["syncs"][number];
type WarmResult = RouterOutputs["admin"]["calendar"]["warm"]["results"][number];

const formatDate = formatNullableDateTimeWithSeconds;

const currentYear = new Date().getFullYear();
const defaultStartDate = `${currentYear}-01-01`;
const defaultEndDate = `${currentYear}-12-31`;

export const parseCalendarCountryCodeInput = (value: string): string[] => {
  const codes = value
    .split(/[\s,;]+/)
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);

  return [...new Set(codes)];
};

export const getGeographySeedStatusLabel = (
  status: CalendarOverview["geographySeed"]["status"],
): string => {
  if (status === "seeded") {
    return "Seeded";
  }

  if (status === "partial") {
    return "Partially seeded";
  }

  return "Not seeded";
};

const getGeographySeedBadgeVariant = (status: CalendarOverview["geographySeed"]["status"]) => {
  if (status === "seeded") {
    return "default" as const;
  }

  if (status === "partial") {
    return "secondary" as const;
  }

  return "destructive" as const;
};

export const CalendarOverviewCards = ({ overview }: { overview: CalendarOverview }) => {
  const counts = overview.geographySeed.counts;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <InfoTile label="Geography seed">
        <div className="mt-2 flex items-center gap-2">
          <Badge variant={getGeographySeedBadgeVariant(overview.geographySeed.status)}>
            {getGeographySeedStatusLabel(overview.geographySeed.status)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            CLDR {overview.geographySeed.latestSourceVersion ?? "unknown"}
          </span>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Last updated {formatDate(overview.geographySeed.latestUpdatedAt)}
        </div>
      </InfoTile>
      <InfoTile
        label="Seeded countries"
        value={`${counts.countries} countries / ${counts.tlds} TLDs`}
      />
      <InfoTile
        label="Seeded languages"
        value={`${counts.languages} languages / ${counts.countryLanguages} country languages`}
      />
      <InfoTile
        label="Calendar cache"
        value={`${overview.calendarCache.eventCount} events / ${overview.calendarCache.syncCount} syncs`}
      >
        <div className="mt-2 text-xs text-muted-foreground">
          {overview.calendarCache.failedSyncCount} failed syncs. Latest sync{" "}
          {formatDate(overview.calendarCache.latestSyncAt)}
        </div>
      </InfoTile>
    </div>
  );
};

export const CalendarSyncTable = ({ syncs }: { syncs: CalendarSync[] }) => (
  <TableShell>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Country</TableHead>
          <TableHead>Year</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Events</TableHead>
          <TableHead>Fetched</TableHead>
          <TableHead>Error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {syncs.length > 0 ? (
          syncs.map((sync) => (
            <TableRow key={`${sync.countryCode}-${sync.year}`}>
              <TableCell className="font-mono text-xs">{sync.countryCode}</TableCell>
              <TableCell className="font-mono text-xs">{sync.year}</TableCell>
              <TableCell>
                <StatusPill label={sync.status.replaceAll("_", " ")} />
              </TableCell>
              <TableCell className="font-mono text-xs">{sync.eventCount}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {formatDate(sync.fetchedAt)}
              </TableCell>
              <TableCell className="max-w-[420px] truncate text-xs text-destructive">
                {sync.lastError ?? "-"}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableEmptyRow colSpan={6}>No calendar syncs found.</TableEmptyRow>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableShell>
);

export const CalendarEventsTable = ({ events }: { events: CalendarEvent[] }) => (
  <TableShell>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Country</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Local Name</TableHead>
          <TableHead>Types</TableHead>
          <TableHead>Localizations</TableHead>
          <TableHead>Fetched</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.length > 0 ? (
          events.map((event) => (
            <TableRow key={event.id}>
              <TableCell className="font-mono text-xs">{event.date}</TableCell>
              <TableCell className="font-mono text-xs">{event.countryCode}</TableCell>
              <TableCell className="text-sm font-medium">{event.name}</TableCell>
              <TableCell className="text-sm">{event.localName}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {event.typesJson.length > 0 ? event.typesJson.join(", ") : "-"}
              </TableCell>
              <TableCell className="min-w-48 text-xs text-muted-foreground">
                {event.localizations.length > 0
                  ? event.localizations
                      .map((localization) => `${localization.locale}: ${localization.name}`)
                      .join("; ")
                  : "-"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {formatDate(event.sourceFetchedAt)}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableEmptyRow colSpan={7}>No calendar events found.</TableEmptyRow>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableShell>
);

const WarmResultTable = ({ results }: { results: WarmResult[] }) => (
  <TableShell>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Country</TableHead>
          <TableHead>Year</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Events</TableHead>
          <TableHead>Fetched</TableHead>
          <TableHead>Error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.map((result) => (
          <TableRow key={`${result.countryCode}-${result.year}`}>
            <TableCell className="font-mono text-xs">{result.countryCode}</TableCell>
            <TableCell className="font-mono text-xs">{result.year}</TableCell>
            <TableCell>
              <StatusPill label={result.status.replaceAll("_", " ")} />
            </TableCell>
            <TableCell className="font-mono text-xs">{result.eventCount}</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDate(result.fetchedAt)}
            </TableCell>
            <TableCell className="max-w-[420px] truncate text-xs text-destructive">
              {result.lastError ?? "-"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableShell>
);

export function CalendarOpsPage() {
  const utils = trpc.useUtils();
  const [countryInput, setCountryInput] = useState("NL, BE, DE");
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  const countryCodes = useMemo(() => parseCalendarCountryCodeInput(countryInput), [countryInput]);
  const queryInput = useMemo(
    () => ({ countryCodes, startDate, endDate }),
    [countryCodes, startDate, endDate],
  );

  const overviewQuery = trpc.admin.calendar.overview.useQuery();
  const countriesQuery = trpc.admin.calendar.countries.useQuery();
  const calendarQuery = trpc.admin.calendar.list.useQuery(queryInput, {
    enabled: countryCodes.length > 0,
  });
  const warmMutation = trpc.admin.calendar.warm.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.admin.calendar.overview.invalidate(),
        utils.admin.calendar.list.invalidate(queryInput),
      ]);
    },
  });

  if (overviewQuery.error) {
    return (
      <QueryErrorState
        title="Failed to load calendar diagnostics"
        message={overviewQuery.error.message}
        isForbidden={overviewQuery.error.data?.code === "FORBIDDEN"}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Calendar Operations"
        description="Inspect geography seeding, public holiday cache state, and warm calendar data for addressed markets."
        meta={countriesQuery.data ? `${countriesQuery.data.length} seeded countries` : undefined}
      />

      {overviewQuery.data ? (
        <CalendarOverviewCards overview={overviewQuery.data} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      )}

      <FilterCard title="Calendar cache lookup" columnsClassName="md:grid-cols-4">
        <Input
          value={countryInput}
          onChange={(event) => setCountryInput(event.target.value)}
          placeholder="NL, BE, DE"
          aria-label="Country codes"
        />
        <Input
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          aria-label="Start date"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          aria-label="End date"
        />
        <Button
          onClick={() => warmMutation.mutate(queryInput)}
          disabled={countryCodes.length === 0 || warmMutation.isPending}
        >
          {warmMutation.isPending ? "Warming..." : "Warm cache"}
        </Button>
      </FilterCard>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>Parsed countries:</span>
        {countryCodes.length > 0 ? (
          countryCodes.map((countryCode) => <CodeText key={countryCode}>{countryCode}</CodeText>)
        ) : (
          <span>No countries selected.</span>
        )}
      </div>

      {calendarQuery.error ? (
        <QueryErrorState
          title="Failed to load calendar data"
          message={calendarQuery.error.message}
        />
      ) : null}

      {warmMutation.error ? (
        <QueryErrorState
          title="Failed to warm calendar cache"
          message={warmMutation.error.message}
        />
      ) : null}

      {warmMutation.data ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latest Warm Run</CardTitle>
          </CardHeader>
          <CardContent>
            <WarmResultTable results={warmMutation.data.results} />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Country-Year Syncs</CardTitle>
        </CardHeader>
        <CardContent>
          {calendarQuery.data ? (
            <CalendarSyncTable syncs={calendarQuery.data.syncs} />
          ) : (
            <Skeleton className="h-48" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cached Calendar Events</CardTitle>
        </CardHeader>
        <CardContent>
          {calendarQuery.data ? (
            <CalendarEventsTable events={calendarQuery.data.events} />
          ) : (
            <Skeleton className="h-64" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
